import { Button, GridItem, IconButton, Text } from "@chakra-ui/react";
import { MdAdd } from "react-icons/md";
import { useFieldArray } from "react-hook-form";
import { t } from "i18next";
import { Fragment } from "react";
import { FaTrash } from "react-icons/fa";
import { CONSUMPTION_ITEMS, MEASURES } from "../../../../../OrganizationQuestionnaire/helpers";
import { FormSelect, NumericFormInput } from "../../../../../../components/Forms";
import { NUMBER_OF_ACTION_COLS, NUMBER_OF_STATIC_COLS } from "./ConsumptionsGrid";
import { SCOPES } from "../../helpers";

export const getEmptyRow = (scope) => {
  const customItem = {};
  if (scope === SCOPES.SCOPE_ONE) {
    customItem["consumptionType"] = "";
  } else {
    customItem["countryId"] = "";
    customItem["electricityType"] = "";
  }
  customItem["consumptionValue"] = "";
  customItem["consumptionMeasure"] = "";

  return customItem;
};

function ConsumptionsModalGridItemsSection({ modalData, formData }) {
  const { consumptionTypes, scope, fieldArrayName, consumptionTypesAttr, editData, countries } = modalData;
  const { control, watch, register, errors, resetField } = formData;

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldArrayName
  });
  const fieldArrayValues = watch(fieldArrayName);

  const renderMeasureOptions = (gridRowIndex) => {
    const measureItems = [];
    if (scope === SCOPES.SCOPE_ONE) {
      const foundMeasureItems = consumptionTypes.measures.find(measure =>
        measure.consumptionTypeId === parseInt(fieldArrayValues[gridRowIndex].consumptionType))?.measures ?? [];
      measureItems.push(...foundMeasureItems);
    } else {
      const foundMeasureItems = consumptionTypes.measures.find(measure =>
        measure.electricityType === fieldArrayValues[gridRowIndex].electricityType)?.measures ?? [];
      measureItems.push(...foundMeasureItems);
    }

    return measureItems.map(optItem => {
      return <option key={`consumptionMeasure-${gridRowIndex}-${optItem.id}`} value={optItem.id}>
        {MEASURES[optItem.code] ?? optItem.name}
      </option>;
    }) ?? [];
  };

  return (
    <Fragment>
      {fields.map((field, index) => {
        const gridItems = [];

        if (scope === SCOPES.SCOPE_ONE) {
          // Adding consumption type grid item
          const consumptionTypeSelector = {
            ...register(`${fieldArrayName}.${index}.consumptionType`, { required: t("errors.required") })
          };
          const typeOfConsumption = fieldArrayName.toLowerCase().includes("stationary") ||
          fieldArrayName.toLowerCase().includes("mobile") ? "stationaryMobileTypes" :
            fieldArrayName.toLowerCase().includes("refrigerant") ? "refrigerantTypes" : "";
          gridItems.push(
            <FormSelect
              {...consumptionTypeSelector}
              onChange={(e) => {
                consumptionTypeSelector.onChange(e);
                // When consumption type is changed, consumptionMeasure field is autopopulated with the first
                // available option from the measures list of the current consumption type
                const firstMeasureValue = consumptionTypes.measures.find(measure =>
                  measure.consumptionTypeId === parseInt(e.target.value))?.measures?.[0]?.id ?? "";
                // resetField is used in order to reevaluate the isValid state of the field
                resetField(`${fieldArrayName}.${index}.consumptionMeasure`, { defaultValue: firstMeasureValue });
              }}
              fontSize="sm"
              fontWeight="semibold"
              errors={errors}
              title={CONSUMPTION_ITEMS[fieldArrayValues[index]?.consumptionType] ?? fieldArrayValues[index]?.consumptionType}
            >
              {
                consumptionTypes[typeOfConsumption].map(optItem => {
                  return <option key={`consumptionType-${index}-${optItem.id}`} value={optItem.id}>
                    {CONSUMPTION_ITEMS[optItem.code] ?? optItem.name}
                  </option>;
                })
              }
            </FormSelect>
          );
        } else if (scope === SCOPES.SCOPE_TWO) {
          // Adding country grid item
          gridItems.push(<FormSelect
            {...register(`${fieldArrayName}.${index}.countryId`, { required: t("errors.required") })}
            fontSize="sm"
            fontWeight="semibold"
            errors={errors}
            title={countries?.find(el => el.id === fieldArrayValues[index]?.countryId)?.name ??
              fieldArrayValues[index]?.countryId}
          >
            {countries?.map(country => {
              return <option key={`countryOption-${country.id}`} value={country.id}>
                {country.name ?? country.code ?? ""}
              </option>;
            }) ?? []}
          </FormSelect>);

          // Adding electricity type grid item
          const electricityTypeSelector = {
            ...register(`${fieldArrayName}.${index}.electricityType`, { required: t("errors.required") })
          };
          gridItems.push(<FormSelect
            {...electricityTypeSelector}
            onChange={(e) => {
              electricityTypeSelector.onChange(e);
              // When consumption type is changed, consumptionMeasure field is autopopulated with the first
              // available option from the measures list of the current consumption type
              const firstMeasureValue = consumptionTypes.measures.find(measure =>
                measure.electricityType === e.target.value)?.measures?.[0]?.id ?? "";
              // resetField is used in order to reevaluate the isValid state of the field
              resetField(`${fieldArrayName}.${index}.consumptionMeasure`, { defaultValue: firstMeasureValue });
            }}
            fontSize="sm"
            fontWeight="semibold"
            errors={errors}
            title={CONSUMPTION_ITEMS[fieldArrayValues[index]?.electricityType] ?? fieldArrayValues[index]?.electricityType}
          >
            {consumptionTypes["electricityTypes"].map(optItem => {
              return <option key={`electricityType-${index}-${optItem}`} value={optItem}>
                {CONSUMPTION_ITEMS[optItem] ?? optItem}
              </option>;
            })}
          </FormSelect>);
        }


        // Adding consumption value grid item
        gridItems.push(<NumericFormInput
          {...register(`${fieldArrayName}.${index}.consumptionValue`, { required: t("errors.required") })}
          fontSize="sm"
          fontWeight="semibold"
          errors={errors}
          title={fieldArrayValues[index]?.consumptionValue ?? ""}
        />);

        // Adding consumption measure grid item
        gridItems.push(<FormSelect
          {...register(`${fieldArrayName}.${index}.consumptionMeasure`, { required: t("errors.required") })}
          fontSize="sm"
          fontWeight="semibold"
          errors={errors}
          title={MEASURES[fieldArrayValues[index]?.consumptionMeasure] ?? fieldArrayValues[index]?.consumptionMeasure}
        >
          {renderMeasureOptions(index)}
        </FormSelect>);

        // Adding row removal button if it is not edit modal
        if (!editData) {
          gridItems.push(<IconButton
            type="button"
            icon={<FaTrash size="18" />}
            width="100%"
            color="esg_red"
            variant="ghost"
            disabled={fields.length === 1}
            onClick={() => {
              remove(index);
            }}
          />);
        }

        return gridItems.map((gridItem, gridItemIndex) => {
          // Total number of columns is sum of static columns, country column (if we are in scope two case) and action column
          const numberOfColumns = NUMBER_OF_STATIC_COLS + (scope === SCOPES.SCOPE_TWO ? 1 : 0) + (editData ? 0 : NUMBER_OF_ACTION_COLS);

          return <GridItem
            key={`${gridItemIndex}-${scope === SCOPES.SCOPE_ONE ? consumptionTypesAttr : "indirect"}`}
            borderWidth={"1px"}
            pl="4" pr="4" pt="2" pb="2"
            display="flex"
            alignItems="center"
            colSpan={editData ? 2 : gridItemIndex === numberOfColumns - 1 ? 1 : 2}
          >
            {gridItem}
          </GridItem>;
        });
      })}

      {/* Last grid item (row) is spanning over all the columns and this is how we calculate it. */}
      {/* We have 1 column (the action column) with colSpan = 1, and all other columns have colSpan = 2 */}
      {/* The columns with colSpan = 2 are Category, Value and Measure (and Country if we are in scope two) */}
      {!editData && <GridItem
        colSpan={(NUMBER_OF_STATIC_COLS + (scope === SCOPES.SCOPE_TWO ? 1 : 0)) * 2 + NUMBER_OF_ACTION_COLS}
        borderWidth={"1px"}
        pr="4" pt="2" pb="2"
        display="flex"
        alignItems="center"
      >
        <Button
          variant="ghost"
          display="flex"
          alignItems="center"
          onClick={() => {
            append(getEmptyRow(scope), { shouldFocus: false });
          }}
          _focus={{
            outline: "none",
            textDecoration: "underline"
          }}
        >
          <IconButton
            as="div"
            variant="outline"
            borderRadius="full"
            size="sm"
            tabIndex={-1}
            pointerEvents="none"
            flexShrink="0"
            icon={<MdAdd size="20" />}
          />
          <Text marginInlineStart="2">{t("buttons.addAnotherItem")}</Text>
        </Button>
      </GridItem>}
    </Fragment>
  );
}

export default ConsumptionsModalGridItemsSection;